// Palworld TCG investing + collecting keyword cluster (2026).
//
// Two topics only, each expanding into exactly five AEO-shaped articles
// (overview, step-by-step, reference, mistakes, FAQ) so the structure matches
// src/lib/guides-merch-2026.ts and the five-articles-per-guide site rule.
//
// Nothing about price levels, print runs or future value is asserted — every
// claim is a checkable process claim (where to read live prices, how grading
// and storage work, how to verify a printing) so the pages stay compliant and
// do not become stale.

import type { Guide, GuideArticle } from "./guides-data";
import { OFFICIAL_LINKS } from "./official-links";

interface Topic {
  slug: string;
  title: string;
  desc: string;
  keywords: string;
  hub: string;
  hubLabel: string;
  tag: string;
  question: string;
  answer: string;
  steps: [string, string][];
  data: [string, string][];
  pitfalls: [string, string][];
  faq: [string, string][];
}

const TOPICS: Topic[] = [
  {
    slug: "palworld-tcg-investing-guide",
    title: "Palworld TCG Investing Guide",
    desc: "How to evaluate Palworld TCG cards as long-term holds: which printings carry scarcity, how to read the price hub, when grading pays for itself, and the risks of buying a new set on release.",
    keywords:
      "palworld tcg investing, palworld card game investing, palworld tcg card value, are palworld cards a good investment, palworld tcg sealed product, palworld card prices, palworld tcg chase cards, palworld tcg market",
    hub: "/prices",
    hubLabel: "Palworld TCG price hub",
    tag: "TCG",
    question: "Are Palworld TCG cards worth investing in?",
    answer:
      "Palworld TCG cards behave like any young trading-card market: a small number of chase printings (SSP, OSR, SR and promo parallels) hold most of the value, while base commons stay near bulk regardless of the Pal on them. Treat it as speculation, not savings — buy printings that are already scarce for a structural reason, track them on the Pal Stars price hub at /prices instead of a remembered figure, and never spend money you need back on a specific date.",
    steps: [
      [
        "Decide sealed or singles before you spend anything",
        "Sealed boxes are a bet on the whole set and on nobody reprinting it; singles are a bet on one card. Sealed needs storage and patience, singles need condition discipline. Mixing both without a plan is how collections end up illiquid.",
      ],
      [
        "Read the live market, never a remembered price",
        "Open /prices and check the median across marketplaces plus the recent sold points for the exact printed code. A single high asking price is not a market; the median of completed sales is.",
      ],
      [
        "Buy on scarcity you can verify, not on hype",
        "The verifiable scarcity signals are the printing tier (SSP/OSR/SR parallel versus base), promo distribution (event-only or product-insert), and whether the card is legal and played in current organized play. Anything else is a story.",
      ],
      [
        "Grade only when the grade changes the buyer",
        "Grading costs a fee plus shipping and weeks of turnaround. It pays when the raw card already carries a premium and a high grade unlocks a different buyer pool. On a base common it never pays.",
      ],
      [
        "Write down your exit before you buy",
        "Pick the condition, the marketplace and the rough window you would sell in. Holders without an exit hold through every peak, which is the most common way a card-game position loses money.",
      ],
    ],
    data: [
      ["Where to check current prices", "The Pal Stars price hub at /prices aggregates marketplace quotes per printed card code and shows recent sold points."],
      ["Card codes to search", "Always use the exact printed English code with the leading E — for example EBP01-001, ETD01-012TSP or ESOUL-002 — so you are pricing the right printing."],
      ["Printing tiers that carry value", "Special/secret parallels (SSP, OSR), super rares (SR) and event or insert promos; base commons and uncommons trade near bulk."],
      ["Per-card reference pages", "Every printed base card has its own page at /cards/<CODE> with the official scan, rules text and its parallel printings listed."],
      ["Grading decision rule", "Grade when the raw card already sells at a premium and a high grade reaches a new buyer pool; otherwise the fee exceeds the uplift."],
      ["Sealed storage baseline", "Cool, dry, dark, flat, no weight on the box; shrink damage and dented corners cut sealed value more than age does."],
    ],
    pitfalls: [
      [
        "Buying a whole set on release day",
        "Release-window supply is at its highest and prices at their softest once distribution catches up. Wait for the market to settle and check /prices before committing to volume.",
      ],
      [
        "Pricing a parallel using the base card",
        "A parallel printing and its base card share a name but not a market. Match the full printed code, suffix included, or you will overpay or undersell.",
      ],
      [
        "Treating one asking price as the market",
        "Sellers can list any number they like. Value the card off the median of recent completed sales, which is what the price hub surfaces.",
      ],
      [
        "Grading bulk in the hope of a jackpot",
        "Submission fees are per card and do not scale down. A Gem-Mint common is still a common.",
      ],
      [
        "Storing cards in a garage, loft or car",
        "Heat and humidity cycles warp cards and cloud sleeves. Storage failure destroys more card value than market moves do.",
      ],
    ],
    faq: [
      [
        "Are Palworld TCG cards a good investment?",
        "They are speculation. Value concentrates in a handful of scarce printings — SSP, OSR, SR parallels and event promos — while base cards stay near bulk. If you buy, buy verifiable scarcity, track it on /prices, and only spend money you do not need back.",
      ],
      [
        "Which Palworld TCG cards are worth the most?",
        "The special and secret parallels and event promos, because their print distribution is structurally smaller than the base set. Check the exact printed code on its /cards page and then the median on the price hub before you value any card.",
      ],
      [
        "Should I buy sealed Palworld booster boxes or single cards?",
        "Sealed is a bet on the entire set and needs clean storage and time; singles let you target one printing and control condition. Pick one approach per budget rather than splitting across both without a plan.",
      ],
      [
        "Is it worth grading Palworld cards?",
        "Only when the raw card already carries a premium and a high grade opens a different buyer pool. Grading fees, shipping and turnaround are fixed costs that a common card cannot recover.",
      ],
      [
        "How do I track Palworld card prices over time?",
        "Use the price hub at /prices: it stores per-card price points from multiple marketplaces, shows the median, and charts the trend so you can see direction instead of guessing from one listing.",
      ],
    ],
  },
  {
    slug: "palworld-collecting-guide",
    title: "Palworld Collecting Guide",
    desc: "How to collect Palworld cards and merchandise properly: what a complete set actually contains, binder and sleeve choices, condition grading language, and how to verify an item is officially licensed.",
    keywords:
      "palworld collecting, palworld card collecting, how to collect palworld cards, palworld tcg set collection, palworld binder, palworld card sleeves, palworld collectors guide, palworld card condition",
    hub: "/cards",
    hubLabel: "Palworld card database",
    tag: "TCG",
    question: "How do you start collecting Palworld cards?",
    answer:
      "Pick one target before you buy anything — a single set, one Pal, or one printing tier — then work from the printed English card codes rather than card names. Sleeve everything on arrival, store it upright in a binder or a fitted box, and verify each item against the card database at /cards so you never pay for a printing or a product line that does not exist.",
    steps: [
      [
        "Choose a collection goal you can finish",
        "A full base set, one Pal across every printing, or one rarity tier. An open-ended 'everything' goal makes progress invisible and spending unbounded.",
      ],
      [
        "Build your checklist from printed codes",
        "Use the card database at /cards and the per-card pages at /cards/<CODE>. Codes are unambiguous; names repeat across printings and sets.",
      ],
      [
        "Sleeve and store on arrival, not later",
        "Penny sleeve every card as it lands, then a toploader or binder page for anything above a common. Most condition damage happens in the first week of casual handling.",
      ],
      [
        "Learn the condition vocabulary before you trade",
        "Near Mint, Lightly Played, Moderately Played and Damaged describe edge wear, surface scratches and centring. Using the right term is what makes a trade or sale go through cleanly.",
      ],
      [
        "Verify licensing on every non-card item",
        "Official merchandise names its licensee and carries a Pocketpair copyright line. If a listing shows only a render and no licensee, treat it as unlicensed.",
      ],
    ],
    data: [
      ["Start here", "The card database at /cards lists every printed base card; each has a detail page at /cards/<CODE> with the official scan."],
      ["Code format", "Printed English codes carry a leading E — EBP01-001, ETD01-012TSP, ESOUL-002 — and parallels add a suffix to the base number."],
      ["Binder baseline", "Side-loading pages in a strap or zip binder, stored upright; top-loading pages let cards slip out when a binder is shelved flat."],
      ["Condition ladder", "Near Mint, Lightly Played, Moderately Played, Heavily Played, Damaged — driven by edge wear, surface, corners and centring."],
      ["Digital practice binder", "The Pal Stars binder at /binder lets you lay out a 108-pocket collection and plan sections before you commit shelf space."],
      ["Merch licensing check", "Licensed Palworld products name the licensee on the packaging or tag alongside a Pocketpair copyright line."],
    ],
    pitfalls: [
      [
        "Collecting by Pal name instead of card code",
        "Names repeat across sets and parallels. Two 'same' cards can be different printings with different markets; the printed code is the only reliable identifier.",
      ],
      [
        "Shelving a binder flat",
        "Flat storage lets cards migrate in their pockets and bends the whole page block. Store binders upright like books.",
      ],
      [
        "Using top-loading binder pages for valuable cards",
        "Gravity does the rest. Side-loading pages keep cards seated when the binder is moved.",
      ],
      [
        "Buying 'limited variant' items with no licensee",
        "Unlicensed plush, figures and 'exclusive' cards are marketed with scarcity language precisely because the product line does not exist. Verify the licensee first.",
      ],
      [
        "Cleaning or trimming a card",
        "Any alteration destroys value and makes a card ungradeable and untradeable. Leave surface marks alone and price the card as it is.",
      ],
    ],
    faq: [
      [
        "How do you start a Palworld card collection?",
        "Pick one finishable goal — a base set, one Pal, or one rarity tier — build the checklist from the printed codes in the card database at /cards, and sleeve every card as it arrives. Working from codes rather than names is what keeps a checklist accurate.",
      ],
      [
        "What is the best way to store Palworld cards?",
        "Penny sleeves for everything, toploaders for premium singles, and side-loading pages in a binder stored upright in a cool, dry, dark place. Avoid lofts, garages and cars, where heat and humidity warp cards.",
      ],
      [
        "How many cards are in a Palworld TCG set?",
        "Set size is set by the printed card list, not by rarity counts, so build your checklist from the codes shown in the card database at /cards rather than from a remembered total. Parallels share a base number with a suffix.",
      ],
      [
        "How do I know if Palworld merchandise is official?",
        "Official products name their licensee on the packaging or tag and carry a Pocketpair copyright line. Render-only photos, no licensee and long overseas shipping windows are the standard signs of an unlicensed copy.",
      ],
      [
        "What does Near Mint mean for a Palworld card?",
        "Near Mint means sharp corners, a clean surface, no visible edge whitening and acceptable centring under normal light. Anything with light edge wear or a small surface mark is Lightly Played, and describing it accurately is what keeps trades smooth.",
      ],
    ],
  },
];

const POOL = OFFICIAL_LINKS;
function pool(offset: number) {
  return [POOL[offset % POOL.length], POOL[(offset + 1) % POOL.length], POOL[(offset + 2) % POOL.length]];
}

function articles(t: Topic): GuideArticle[] {
  const hubSentence = `Everything here pairs with the ${t.hubLabel} at ${t.hub}.`;
  return [
    {
      title: t.title,
      slug: "overview",
      intro: `${t.answer} ${hubSentence}`,
      sections: [
        { heading: t.question, text: t.answer },
        { heading: "Why this matters", text: `${t.steps[0][1]} ${t.pitfalls[0][1]}` },
        { heading: "What is actually checkable", text: t.data.map(([k, v]) => `${k}: ${v}`).join(" ") },
        {
          heading: `Use it with the ${t.hubLabel}`,
          text: `${hubSentence} Open ${t.hub} and work from the live data there rather than a remembered price or set total.`,
        },
      ],
      externalLinks: pool(1),
    },
    {
      title: `${t.title}: step-by-step`,
      slug: "step-by-step",
      intro: "The order to work through, with the mistake each step removes.",
      sections: t.steps.map(([heading, text], i) => ({ heading: `${i + 1}. ${heading}`, text })),
      externalLinks: pool(0),
    },
    {
      title: `${t.title}: reference table`,
      slug: "reference",
      intro: `Confirmed details behind ${t.title.toLowerCase()}, in the order you will need them.`,
      sections: t.data.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(2),
    },
    {
      title: `${t.title}: mistakes and fixes`,
      slug: "mistakes",
      intro: "The failure modes that cost collectors the most money on this topic, each with the fix.",
      sections: t.pitfalls.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(3),
    },
    {
      title: `${t.title}: FAQ`,
      slug: "faq",
      intro: `Direct answers to the questions people ask about ${t.title.toLowerCase()}.`,
      sections: t.faq.map(([q, a]) => ({ heading: q, text: a })),
      externalLinks: pool(4),
    },
  ];
}

export const COLLECTING_GUIDES: Guide[] = TOPICS.map((t) => ({
  slug: t.slug,
  title: `${t.title} & FAQ`,
  desc: t.desc,
  tag: t.tag,
  updated: "2026-08-26",
  keywords: t.keywords,
  articles: articles(t),
}));

/** Hub page each investing/collecting cluster supports, for internal linking. */
export const COLLECTING_CLUSTERS: Record<string, { slug: string; title: string }[]> = TOPICS.reduce(
  (acc, t) => {
    (acc[t.hub] ??= []).push({ slug: t.slug, title: t.title });
    return acc;
  },
  {} as Record<string, { slug: string; title: string }[]>,
);
